import {sendAnalyticsEvent} from '#/analytics/analytics';
import {Modal} from '#/atoms/modal';
import {FormLabelInput} from '#/components/user/form-label-input';
import {ModalContext} from '#/contexts/modal-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import {baseKeysUseQuery} from '#/hooks/query/base/base-keys-use-query';
import {useUserProfilePrivate} from '#/hooks/query/use-user-profile';
import {useFormInputData} from '#/hooks/user-form-input-data';
import {userProfileRequests} from '#/repository/fetch-user-profile';
import {validateEmail} from '#/utils/email-utils';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {FormEvent, FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';

type CompleteUserProfileModalProps = {};
export const CompleteUserProfileModal: FunctionComponent<CompleteUserProfileModalProps> = () => {
  const {t} = useTranslation('translation');
  const queryClient = useQueryClient();

  const {
    web3AuthSignatureResult: {data: web3AuthIdTokenGroup},
  } = useContext(WalletContext);
  const {open, close} = useContext(ModalContext);

  const {data: userProfilePrivate} = useUserProfilePrivate({enabled: false});

  const formInputData = {
    firstName: useFormInputData({initialValue: userProfilePrivate?.firstName, validator: Boolean}),
    lastName: useFormInputData({initialValue: userProfilePrivate?.lastName, validator: Boolean}),
    email: useFormInputData({initialValue: userProfilePrivate?.email, validator: validateEmail}),
  };

  const updateUserProfileMutationEnabled =
    !!userProfilePrivate &&
    Object.values(formInputData).every(({value, isValid}) => value && isValid) &&
    !!web3AuthIdTokenGroup;

  const updateUserProfileMutation = useMutation({
    mutationKey: ['complete-user-profile-mutation'],
    mutationFn: async () => {
      return await userProfileRequests.upsert(
        {
          ...(formInputData.firstName.isModified && {firstName: formInputData.firstName.value}),
          ...(formInputData.lastName.isModified && {lastName: formInputData.lastName.value}),
          ...(formInputData.email.isModified && {email: formInputData.email.value}),
        },
        web3AuthIdTokenGroup!,
      );
    },
    onSuccess: data => {
      queryClient.setQueryData([baseKeysUseQuery.useUserProfilePrivate, data.account], data);

      close();
      open({
        name: 'AddFundsModal',
        props: {},
      });
    },
  });

  const isLoading = updateUserProfileMutation.isPending;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!updateUserProfileMutationEnabled) return;

    sendAnalyticsEvent.completeUserProfile();
    await updateUserProfileMutation.mutateAsync();
  };

  return (
    <Modal title={t('pages.completeUserProfile.title')} theme='dark' onClose={close}>
      <form
        className='p-6 pt-4 flex flex-col items-center gap-3 mx-auto w-full md:w-[340px] md:h-full relative text-white'
        onSubmit={onSubmit}
      >
        <FormLabelInput
          type='text'
          label={t('pages.editUserProfile.fields.firstName.label')}
          placeholder={t('pages.editUserProfile.fields.firstName.placeholder')}
          inputDataValue={formInputData.firstName}
          showCheckmark
        />
        <FormLabelInput
          type='text'
          label={t('pages.editUserProfile.fields.lastName.label')}
          placeholder={t('pages.editUserProfile.fields.lastName.placeholder')}
          inputDataValue={formInputData.lastName}
          showCheckmark
        />
        <FormLabelInput
          type='email'
          label={t('pages.editUserProfile.fields.email.label')}
          placeholder={t('pages.editUserProfile.fields.email.placeholder')}
          inputDataValue={formInputData.email}
          showCheckmark
        />

        <button
          type='submit'
          className={
            'w-full mt-2 px-4 py-2 text-white rounded flex-center bg-deepblue data-[error=true]:bg-downred data-[loading=true]:bg-gray-400 hover:opacity-70 disabled:opacity-50 transition-all duration-200 ease-in-out'
          }
          data-loading={isLoading}
          data-error={!!updateUserProfileMutation.error}
          disabled={!updateUserProfileMutationEnabled || isLoading}
        >
          {isLoading
            ? t('statuses.sending')
            : updateUserProfileMutation.error
              ? t('actions.errorTryAgain')
              : t('actions.next')}
        </button>

        <div className='flex gap-1 text-sm text-gray-400 items-start'>
          <div className='aspect-square flex-center p-1 group'>
            <span className={'w-3 h-3 rounded-full bg-gray-400 text-grayblue text-xs flex-center select-none'}>
              {t('symbols.info')}
            </span>
          </div>
          <span>{t('pages.completeUserProfile.disclaimer')}</span>
        </div>
      </form>
    </Modal>
  );
};
